import ROUTES from '@/ROUTES'

import { AuthToken } from '@/MODELS/types'

export type ApiResponse<DataType> = {
    status?: number
    message?: string
    data?: DataType
    error?: string
}

/* In-memory token storage
 */
let inMemoryAccessToken: string | null = null
let inMemoryTokenType: string | null = null
let inMemoryExpiresAt: number | null = null

const authChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('bp_auth') : null
authChannel?.addEventListener('message', (event) => {
    if (event.data === 'logout') {
        inMemoryAccessToken = null
        inMemoryTokenType = null
        inMemoryExpiresAt = null
        window.location.href = '/login'
    }
})

export function setAccessToken(token: AuthToken) {
    inMemoryAccessToken = token.accessToken
    inMemoryTokenType = token.tokenType
    inMemoryExpiresAt = token.expiresAt
}

export function clearAccessToken() {
    inMemoryAccessToken = null
    inMemoryTokenType = null
    inMemoryExpiresAt = null
    authChannel?.postMessage('logout')
}

export function hasAccessToken(): boolean {
    return inMemoryAccessToken !== null
}

export function getTokens(): AuthToken {
    if (!inMemoryAccessToken) {
        console.error('[ getTokens ] No access token in memory — redirecting to /login')
        window.location.replace('/login')
        throw { status: 401, error: 'Missing tokens' }
    }
    return {
        accessToken: inMemoryAccessToken,
        tokenType: inMemoryTokenType ?? 'Bearer',
        expiresAt: inMemoryExpiresAt ?? 0,
    }
}

export async function tryRefreshAccessToken(): Promise<boolean> {
    try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/${ROUTES.auth.refresh}`, {
            method: 'GET',
            credentials: 'include',
        })
        if (!res.ok) return false
        const body = await res.json()
        if (!body?.accessToken) return false
        setAccessToken(body)
        return true
    } catch {
        return false
    }
}

/* Middleware
 */
export async function wrappedFetch(
    input: RequestInfo | URL,
    init?: RequestInit | undefined,
    numRetries?: number,
    timeoutMs?: number,
    // When true, a 403 is thrown like any other error instead of navigating the
    // whole app to /forbidden. Callers that handle "not allowed" inline (e.g. a
    // role-gated action inside a modal) pass this so a denied action shows a
    // message in place rather than ejecting the user from the page.
    suppressForbiddenRedirect?: boolean
): Promise<Response> {
    // If a timeout is specified, create an AbortController that auto-aborts after timeoutMs.
    // This is the "outer" timeout (e.g., 120s for onboarding) that controls how long the
    // frontend waits before giving up. It should be longer than the backend's OpenAI timeout
    // (60s) + retry time, so the server has a chance to retry with a faster model (gpt-4.1-nano)
    // before the client abandons the request. See ai.service.ts for the full timeout strategy.
    let controller: AbortController | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (timeoutMs) {
        controller = new AbortController()
        timeoutId = setTimeout(() => controller!.abort(), timeoutMs)
    }

    const response = await fetch(input, {
        ...init,
        signal: controller?.signal ?? init?.signal,
    }).finally(() => {
        if (timeoutId) clearTimeout(timeoutId)
    })

    if (response.ok === false) {
        // Not Authorized
        if (response.status === 401) {
            clearAccessToken()
            window.location.replace('/login')
        }

        if (response.status === 403 && !suppressForbiddenRedirect) {
            window.location.assign('/forbidden')
        }

        // Should Refresh - whoami or router middleware will send these redirect responses to attempt a refresh
        if (response.status === 302) {
            const body = await response
                .json()
                .then((body) => body)
                .catch(() => null)

            if (!body) {
                return response
            }

            if (body.shouldRefresh) {
                if (numRetries && numRetries >= 2) {
                    console.log('[ wrappedFetch ] EXCEEDED MAXIMUM REFRESH ATTEMPTS')
                    throw {
                        status: 401,
                        error: 'Maximum retries exceeded',
                    }
                }

                console.log('[ wrappedFetch ] SENDING REFRESH REQUEST...')

                const refreshed = await tryRefreshAccessToken()

                if (!refreshed) {
                    throw {
                        status: 401,
                        error: 'Failed to refresh',
                    }
                }

                // attempt to complete the initial request with new token
                console.log('attempting original request...')
                const tokens = getTokens()
                return await wrappedFetch(
                    input,
                    {
                        ...init,
                        headers: {
                            ...(init?.headers ?? {}),
                            Authorization: `${tokens.tokenType} ${tokens.accessToken}`,
                        },
                    },
                    (numRetries ?? 0) + 1,
                    timeoutMs,
                    suppressForbiddenRedirect
                )
            }

            // other redirects - add if needed
        }

        // All other errors. The parsed body rides along as `data` so callers
        // can read structured error fields (e.g. cross-portal redirect codes)
        // — existing catchers that only destructure { status, error } are
        // unaffected.
        const errorBody = await response
            .json()
            .then((body) => body)
            .catch(() => null)
        throw {
            status: response.status,
            error: errorBody?.error ?? response.statusText,
            data: errorBody,
        }
    }

    // OK responses
    return response
}

/* Request Functions
 */

export async function api<ResponseBodyType>(
    endpoint: string,
    body?: any,
    logging?: boolean,
    timeoutMs?: number,
    suppressForbiddenRedirect?: boolean
): Promise<ApiResponse<ResponseBodyType | null>> {
    const token: AuthToken = getTokens()
    const method = body ? 'POST' : 'GET'
    const requestBody = body ? JSON.stringify(body) : null
    return await wrappedFetch(
        `${import.meta.env.VITE_API_URL}/${endpoint}`,
        {
            method: method,
            headers: {
                Authorization: `${token.tokenType} ${token.accessToken}`,
                'Content-Type': 'application/json',
            },
            body: requestBody,
        },
        undefined,
        timeoutMs,
        suppressForbiddenRedirect
    )
        .then(async (response: Response) => {
            if (logging || import.meta.env.VITE_API_LOGGING == 'true') {
                console.log(`[ api.service ] ${method} api/${endpoint}`, { response })
            }

            let body = undefined

            try {
                body = await response.json()
            } catch {}

            return {
                status: response.status,
                message: response.statusText,
                data: body,
            }
        })
        .catch((errorResponse: ApiResponse<null>) => {
            // response is formed by the middleware
            if (errorResponse?.status === 401) {
                // has attempted and failed to refresh
                clearAccessToken()
                window.location.replace('/login')
            }
            return errorResponse
        })
}

export async function api_no_auth<ResponseBodyType>(
    endpoint: string,
    body?: any,
    logging?: boolean,
    timeoutMs?: number,
    suppressForbiddenRedirect?: boolean
): Promise<ApiResponse<ResponseBodyType | null>> {
    const method = body ? 'POST' : 'GET'
    const requestBody = body ? JSON.stringify(body) : null

    return await wrappedFetch(
        `${import.meta.env.VITE_API_URL}/${endpoint}`,
        {
            method: method,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: requestBody,
        },
        undefined,
        timeoutMs,
        suppressForbiddenRedirect
    )
        .then(async (response: Response) => {
            if (logging || import.meta.env.VITE_API_LOGGING == 'true') {
                console.log(`[ api.service ] **NO AUTH REQUEST** ${method} api/${endpoint}`, { response })
            }

            let body = undefined

            try {
                body = await response.json()
            } catch {}

            return {
                status: response.status,
                message: response.statusText,
                data: body,
            }
        })
        .catch((errorResponse: ApiResponse<null>) => {
            // response is formed by the middleware
            return errorResponse
        })
}

export async function api_patch<ResponseBodyType>(endpoint: string, body: any): Promise<ApiResponse<ResponseBodyType | null>> {
    const token: AuthToken = getTokens()
    return await wrappedFetch(`${import.meta.env.VITE_API_URL}/${endpoint}`, {
        method: 'PATCH',
        headers: {
            Authorization: `${token.tokenType} ${token.accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    })
        .then(async (response: Response) => {
            let body = undefined
            try { body = await response.json() } catch {}
            return { status: response.status, message: response.statusText, data: body }
        })
        .catch((errorResponse: ApiResponse<null>) => {
            if (errorResponse?.status === 401) {
                clearAccessToken()
                window.location.replace('/login')
            }
            return errorResponse
        })
}

export async function api_delete(endpoint: string, logging?: boolean): Promise<ApiResponse<null>> {
    const token: AuthToken = getTokens()

    return await wrappedFetch(`${import.meta.env.VITE_API_URL}/${endpoint}`, {
        method: 'DELETE',
        headers: {
            Authorization: `${token.tokenType} ${token.accessToken}`,
            'Content-Type': 'application/json',
        },
    })
        .then(async (response: Response) => {
            if (logging || import.meta.env.VITE_API_LOGGING == 'true') {
                console.log(`[ api.service ] DELETE api/${endpoint}`, { response })
            }

            return {
                status: response.status,
                message: response.statusText,
            }
        })
        .catch((errorResponse: ApiResponse<null>) => {
            // response is formed by the middleware
            if (errorResponse?.status === 401) {
                // has attempted and failed to refresh
                clearAccessToken()
                window.location.replace('/login')
            }
            return errorResponse
        })
}

export async function api_stream(
    endpoint: string,

    abortSignal?: AbortSignal,

    body?: any
): Promise<ReadableStreamDefaultReader<Uint8Array> | null> {
    const token: AuthToken = getTokens()

    const method = body ? 'POST' : 'GET'

    const requestBody = body ? JSON.stringify(body) : null

    const response = await wrappedFetch(`${import.meta.env.VITE_API_URL}/${endpoint}`, {
        method: method,

        body: requestBody,

        headers: {
            Authorization: `${token.tokenType} ${token.accessToken}`,

            'Content-Type': 'application/json',
        },

        signal: abortSignal,
    })

    return response.body?.getReader() ?? null
}
